import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { RequireStaff } from "@/components/RequireStaff";
import { useStaffDeals } from "@/hooks/useStaffDeals";
import { StaffKpiCards } from "@/components/staff/StaffKpiCards";
import { StaffDealsTable } from "@/components/staff/StaffDealsTable";
import { MyWorkTabs, type MyWorkTab } from "@/components/staff/MyWorkTabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Filter, RefreshCw, Briefcase } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const T = {
  en: {
    title: "My Work",
    subtitle: "All your deals, submissions and triage in one place",
    search: "Search deals...",
    priority: "Priority",
    all: "All", high: "High", normal: "Normal", low: "Low",
    refresh: "Refresh",
    loading: "Loading...",
    triageHint: "Unassigned new submissions. Take one to start the review.",
    todoHint: "Deals assigned to you that need action.",
    activeHint: "All your active deals across all statuses.",
  },
  fr: {
    title: "Mon Travail",
    subtitle: "Tous vos dossiers, soumissions et triage au même endroit",
    search: "Rechercher...",
    priority: "Priorité",
    all: "Tous", high: "Haute", normal: "Normale", low: "Basse",
    refresh: "Actualiser",
    loading: "Chargement...",
    triageHint: "Nouvelles soumissions non assignées. Prenez-en une pour commencer.",
    todoHint: "Dossiers qui vous sont assignés et qui requièrent une action.",
    activeHint: "Tous vos dossiers actifs, tous statuts confondus.",
  },
};

// Map onglet -> vue useStaffDeals existante
const VIEW_MAP = {
  todo: "todo" as const,
  triage: "triage" as const,
  active: "todo" as const, // "Active" = mes deals assignés tous statuts (todo déjà filtré assigné)
};

export default function MyWork() {
  const { language } = useLanguage();
  const t = T[language] || T.en;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialTab = (searchParams.get("tab") as MyWorkTab) || "todo";
  const [tab, setTab] = useState<MyWorkTab>(initialTab);
  const [searchTerm, setSearchTerm] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");

  const { deals, loading, kpis, takeDeal, refetch } = useStaffDeals(VIEW_MAP[tab]);

  useEffect(() => {
    if (searchParams.get("tab") !== tab) {
      setSearchParams({ tab }, { replace: true });
    }
  }, [tab]);

  const filteredDeals = deals.filter((d) => {
    const s = searchTerm.toLowerCase();
    const matchesSearch = d.title.toLowerCase().includes(s) || d.id.toLowerCase().includes(s);
    const matchesPriority = priorityFilter === "all" || d.priority === priorityFilter;
    return matchesSearch && matchesPriority;
  });

  const counts = {
    todo: kpis.assignedDeals,
    triage: kpis.triageCount,
    active: kpis.assignedDeals,
  };

  const hint = tab === "triage" ? t.triageHint : tab === "todo" ? t.todoHint : t.activeHint;

  return (
    <RequireStaff>
      <div className="space-y-6 font-['Geist',sans-serif]">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Briefcase className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{t.title}</h1>
              <p className="text-gray-400">{t.subtitle}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch?.()}
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t.refresh}
          </Button>
        </header>

        <MyWorkTabs active={tab} onChange={setTab} counts={counts} />

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <p className="text-amber-200 text-sm">{hint}</p>
        </div>

        <StaffKpiCards kpis={kpis} view={tab === "triage" ? "triage" : "todo"} />

        <div className="flex flex-col sm:flex-row gap-3 bg-gray-800/30 p-4 rounded-xl border border-gray-700/50">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder={t.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
            />
          </div>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-full sm:w-[150px] bg-gray-900 border-gray-700 text-white">
              <Filter className="w-4 h-4 mr-2 text-gray-500" />
              <SelectValue placeholder={t.priority} />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="all">{t.all}</SelectItem>
              <SelectItem value="high">{t.high}</SelectItem>
              <SelectItem value="normal">{t.normal}</SelectItem>
              <SelectItem value="low">{t.low}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="bg-gray-800/50 rounded-xl p-8 text-center">
            <p className="text-gray-400 animate-pulse">{t.loading}</p>
          </div>
        ) : (
          <StaffDealsTable
            deals={filteredDeals}
            view={tab === 'active' ? 'all' : tab}
            onTakeDeal={takeDeal}
            onViewDeal={(id) => navigate(`/staff/deals/${id}`)}
          />
        )}
      </div>
    </RequireStaff>
  );
}